// Transaction Reconciliation (Merchant RPA) Agent.
//
//   - Hero header with status pill, label, and quick actions
//   - Compact tab strip with icons
//   - Per-tab info banner
//   - Tabs:
//       Dashboard   — live observability + run trend
//       Merchants   — merchant CRUD + encrypted credentials
//       Workflows   — versioned recordings, ad-hoc runs
//       Record      — manual builder + JSON editor (noVNC slot pending)
//       Runs        — replay engine with step-by-step timing + screenshots
//       Files       — raw files captured by recordings + runs
//       Invoices    — extracted invoices with inline reconciliation
//                     (Approve / Unlink / Find-match per row + Run
//                     auto-match button). Replaces both the legacy
//                     Documents tab and the standalone Reconcile tab.
//       Rerun       — view failed runs + one-click rerun
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, Stack, Chip, Alert, Button,
    CircularProgress, Tabs, Tab, Tooltip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import VideocamIcon from '@mui/icons-material/Videocam';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LaunchIcon from '@mui/icons-material/Launch';
import ObservabilityDashboard from './ObservabilityDashboard';
import MerchantList from './MerchantList';
import WorkflowList from './WorkflowList';
import RunList from './RunList';
import FileList from './FileList';
// DocumentList + ReconciliationView are both retired. The new
// InvoiceList tab folds them together:
//   - Per-invoice listing (Documents → flattened line_items)
//   - Inline reconciliation: match status column, Approve / Unlink /
//     Find-match actions, "Run auto-match" button, merchant+bank scope.
// Both component files are kept on disk for reference / rollback but
// neither is imported here.
import InvoiceList from './InvoiceList';
import ErrorLogs from './ErrorLogs';
import RecorderModal from './RecorderModal';
import { rpaMerchantList } from './api';
import { RPA_COLORS, RPA_FONT } from './rpaTheme';

const TABS = [
    { label: 'Dashboard',     icon: <DashboardIcon fontSize="small" /> },
    { label: 'Merchants',     icon: <StorefrontIcon fontSize="small" /> },
    { label: 'Record',        icon: <VideocamIcon fontSize="small" /> },
    { label: 'Workflows',     icon: <AutoAwesomeMotionIcon fontSize="small" /> },
    { label: 'Runs',          icon: <PlayArrowIcon fontSize="small" /> },
    { label: 'Files',         icon: <InsertDriveFileIcon fontSize="small" /> },
    { label: 'Invoices',      icon: <DescriptionIcon fontSize="small" /> },
    { label: 'Rerun',         icon: <ErrorOutlineIcon fontSize="small" /> },
];

export default function MerchantRPAAgent() {
    const [tab, setTab] = useState(0);
    const [merchants, setMerchants] = useState([]);
    const [merchantsLoading, setMerchantsLoading] = useState(false);
    const [recorderOpen, setRecorderOpen] = useState(false);

    const loadMerchants = useCallback(async () => {
        setMerchantsLoading(true);
        try {
            const r = await rpaMerchantList({ per_page: 100 });
            if (r?.data?.success) setMerchants(r.data.data || []);
        } catch { /* non-blocking */ }
        finally { setMerchantsLoading(false); }
    }, []);

    useEffect(() => { if (tab === 2 || tab === 3) loadMerchants(); }, [tab, loadMerchants]);

    return (
        <Box sx={{ fontFamily: RPA_FONT }}>
            <Hero />

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: `1px solid ${RPA_COLORS.border}`,
                    overflow: 'hidden',
                }}
            >
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: `1px solid ${RPA_COLORS.border}`,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 13,
                            minHeight: 48,
                            px: 2.5,
                        },
                        '& .Mui-selected': { color: `${RPA_COLORS.accent} !important` },
                        '& .MuiTabs-indicator': { backgroundColor: RPA_COLORS.accent, height: 3 },
                    }}
                >
                    {TABS.map((t, i) => (
                        <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />
                    ))}
                </Tabs>

                <Box sx={{ p: { xs: 1.5, md: 2.5 }, background: RPA_COLORS.surfaceAlt, minHeight: 480 }}>
                    {tab === 0 && (
                        <>
                            <PhaseHeader message="Live observability across the workflow: 24h success rate, error queue size, 30-day Gemini cost, top failing workflows, and run trend. The schedule tick fires due cron schedules every 60s; the run dispatcher drains queued runs every 30s." />
                            <ObservabilityDashboard />
                        </>
                    )}
                    {tab === 1 && (
                        <>
                            <PhaseHeader message="Merchant CRUD and encrypted credentials are live. Add a merchant before recording workflows." />
                            <MerchantList />
                        </>
                    )}
                    
                    {tab === 2 && (
                        <>
                            <PhaseHeader message="Manual step builder and JSON editor are live. The noVNC in-browser recorder slot waits on operator setup (Xvfb + websockify on the worker host)." />
                            <RecordTabBody
                                merchants={merchants}
                                loading={merchantsLoading}
                                onOpenRecorder={() => setRecorderOpen(true)}
                            />
                            <RecorderModal
                                open={recorderOpen}
                                onClose={() => setRecorderOpen(false)}
                                merchants={merchants}
                                onSaved={() => { setRecorderOpen(false); setTab(2); }}
                            />
                        </>
                    )}
                    {tab === 3 && (
                        <>
                            <PhaseHeader message="Workflows list versioned recordings. Click Run to enqueue an ad-hoc execution; the worker picks up queued runs every 30 seconds." />
                            <WorkflowList />
                        </>
                    )}
                    {tab === 4 && (
                        <>
                            <PhaseHeader message="Replay engine and Gemini-vision selector self-heal are live. Click a row to see step-by-step timing, screenshots, and any healed selectors." />
                            <RunList />
                        </>
                    )}
                    {tab === 5 && (
                        <>
                            <PhaseHeader message="Raw files captured by workflow runs and recordings. Click Extract information on any file to push it through the document pipeline — the structured invoices will then appear in the Invoices tab." />
                            <FileList onJumpToDocuments={() => setTab(6)} />
                        </>
                    )}
                    {tab === 6 && (
                        <>
                            <PhaseHeader message="Structured invoices + inline reconciliation. Each row carries its match status; use the Match column to Approve / Unlink / Find-match, or click Run auto-match to score every invoice against bank transactions. Multi-row spreadsheet exports (purchase reports, statement bundles) appear as one row per invoice." />
                            <InvoiceList />
                        </>
                    )}
                    {tab === 7 && (
                        <>
                            <PhaseHeader message="Failed runs — broken selectors, expired sessions, policy denials. Click Rerun on any row to mark it handled and queue a fresh execution. If the new run fails too, it shows up here for another retry." />
                            <ErrorLogs />
                        </>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}


function Hero() {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${RPA_COLORS.border}`,
                background: `linear-gradient(135deg, ${RPA_COLORS.surface} 0%, ${RPA_COLORS.surfaceAlt} 100%)`,
                p: { xs: 2.5, md: 3 },
                mb: 2,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box sx={{
                position: 'absolute',
                top: -40, right: -40,
                width: 200, height: 200,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${RPA_COLORS.robotSoft} 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Box sx={{
                    width: 56, height: 56,
                    borderRadius: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `linear-gradient(135deg, ${RPA_COLORS.robot} 0%, ${RPA_COLORS.accent} 100%)`,
                    color: '#fff',
                    boxShadow: '0 6px 16px rgba(99,102,241,0.30)',
                    flexShrink: 0,
                }}>
                    <PrecisionManufacturingIcon sx={{ fontSize: 30 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Chip
                            label="Live"
                            size="small"
                            sx={{
                                fontWeight: 700,
                                fontSize: 11,
                                color: RPA_COLORS.success,
                                bgcolor: 'rgba(22, 163, 74, 0.12)',
                                border: `1px solid ${RPA_COLORS.success}33`,
                                height: 22,
                            }}
                        />
                    </Stack>
                    <Typography sx={{
                        fontFamily: RPA_FONT,
                        fontWeight: 700,
                        fontSize: { xs: 22, md: 26 },
                        color: RPA_COLORS.text,
                        lineHeight: 1.2,
                    }}>
                        Merchant RPA Agent
                    </Typography>
                    <Typography sx={{ color: RPA_COLORS.textMuted, fontSize: 14, mt: 0.5, maxWidth: 720 }}>
                        Train a robot to log into merchant portals, pull invoices and statements,
                        parse them into structured data, and reconcile against bank and GL transactions —
                        unattended, with full audit trail.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Tooltip title="View developer documentation">
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LaunchIcon fontSize="small" />}
                            href="/documentation/Transaction_Reconciliation_Developer_Doc.md"
                            target="_blank"
                            sx={{
                                textTransform: 'none',
                                borderColor: RPA_COLORS.border,
                                color: RPA_COLORS.text,
                                '&:hover': {
                                    borderColor: RPA_COLORS.accent,
                                    backgroundColor: RPA_COLORS.accentSoft,
                                },
                            }}
                        >
                            Docs
                        </Button>
                    </Tooltip>
                </Stack>
            </Stack>
        </Paper>
    );
}


function PhaseHeader({ message }) {
    if (!message) return null;
    return (
        <Box sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1.5,
            backgroundColor: RPA_COLORS.accentSoft,
            border: `1px solid ${RPA_COLORS.accentBorder}`,
        }}>
            <Typography variant="body2" sx={{ color: RPA_COLORS.text }}>
                {message}
            </Typography>
        </Box>
    );
}


function RecordTabBody({ merchants, loading, onOpenRecorder }) {
    const hasActiveMerchant = merchants.some((m) => m.is_active);
    console.log(merchants)
    console.log(hasActiveMerchant)
    return (
        <Paper sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 2,
            border: `1px solid ${RPA_COLORS.border}`,
            background: `linear-gradient(180deg, ${RPA_COLORS.surface} 0%, ${RPA_COLORS.surfaceMuted} 100%)`,
        }}>
            <Box sx={{
                width: 72, height: 72, mx: 'auto', mb: 2,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${RPA_COLORS.robot} 0%, ${RPA_COLORS.accent} 100%)`,
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(99,102,241,0.25)',
            }}>
                <VideocamIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography variant="h6" sx={{ fontFamily: RPA_FONT, fontWeight: 700, mb: 1 }}>
                Build or record a new workflow
            </Typography>
            <Typography variant="body2" sx={{
                color: RPA_COLORS.textMuted, maxWidth: 560, mx: 'auto', mb: 3,
            }}>
                Define a sequence of browser actions — navigate, type, click, download —
                that runs unattended on a schedule. Use the manual builder now;
                switch to in-browser recording once your worker host is set up.
            </Typography>
            <Button
                variant="contained" size="large"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VideocamIcon />}
                onClick={onOpenRecorder}
                disabled={loading || !hasActiveMerchant}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3, py: 1,
                    background: `linear-gradient(135deg, ${RPA_COLORS.robot} 0%, ${RPA_COLORS.accent} 100%)`,
                    boxShadow: '0 4px 14px rgba(99,102,241,0.30)',
                    '&:hover': {
                        background: `linear-gradient(135deg, ${RPA_COLORS.accent} 0%, ${RPA_COLORS.robot} 100%)`,
                    },
                }}
            >
                Open Recorder
            </Button>
            {!loading && !hasActiveMerchant && (
                <Alert severity="warning" sx={{ mt: 3, maxWidth: 560, mx: 'auto', textAlign: 'left' }}>
                    Add at least one active merchant in the <strong>Merchants</strong> tab
                    before you can build a workflow.
                </Alert>
            )}
        </Paper>
    );
}


